// Phase 1 — Admin Email Marketing module
// =======================================
// Centralized contact DB view + Mailchimp CSV import. Reuses the existing
// black-and-gold AgentLayout so the Admin visual language stays consistent.
// Phase 2 (campaign builder) and Phase 3 (popup swap) will attach additional
// tabs to this page without touching the rest of the application.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  CaretLeft, EnvelopeSimple, Upload, Users, UserPlus, Prohibit, Broom,
  MagnifyingGlass, Trash, PencilSimple, Download, CheckCircle, Warning,
  FileArrowUp, Clock, Tag,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { AgentLayout } from "@/components/AgentLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";

const STATUS_CHIP = {
  subscribed: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
  unsubscribed: "bg-rose-500/15 text-rose-200 border-rose-500/30",
  cleaned: "bg-amber-500/15 text-amber-200 border-amber-500/30",
  pending: "bg-sky-500/15 text-sky-200 border-sky-500/30",
};

const SOURCE_LABEL = {
  travel_club_popup: "Travel Club",
  direct_client: "Direct",
  advisor_client: "Advisor",
  mailchimp_import: "Mailchimp Import",
  manual_entry: "Manual",
};

const inputCls =
  "bg-black/40 border-white/15 text-white placeholder:text-white/30 focus:border-[#D4AF37] font-sans";

// ---------------------------------------------------------------------------
// KPI tile
// ---------------------------------------------------------------------------
function Kpi({ label, value, icon: Icon, tone = "default", testId }) {
  const toneCls = {
    default: "text-white",
    gold: "text-[#D4AF37]",
    rose: "text-rose-300",
    amber: "text-amber-300",
    emerald: "text-emerald-300",
  }[tone];
  return (
    <div
      className="relative overflow-hidden rounded-xl border border-white/10 bg-black/40 p-4 sm:p-5"
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] uppercase tracking-[0.3em] text-white/40 font-sans">
          {label}
        </span>
        {Icon && <Icon size={14} weight="duotone" className="text-white/30" />}
      </div>
      <div className={`font-serif text-3xl sm:text-4xl ${toneCls}`}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Import dialog — upload CSV → preview → commit
// ---------------------------------------------------------------------------
function ImportDialog({ open, onOpenChange, onCommitted }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      toast.error("Please choose a .csv file");
      return;
    }
    setFile(f);
    setPreview(null);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        "/admin/email-marketing/import/preview",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPreview(data);
      toast.success(`Previewed ${data.total_rows} rows — ${data.valid_rows} valid`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not preview CSV");
    } finally {
      setUploading(false);
    }
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const { data } = await api.post(
        `/admin/email-marketing/import/${preview.id}/commit`,
      );
      toast.success(
        `Imported: ${data.inserted} new, ${data.updated} updated, ${data.skipped} skipped`,
      );
      reset();
      onOpenChange(false);
      onCommitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent
        className="max-w-3xl bg-black border border-white/10"
        data-testid="import-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white font-serif text-2xl">
            Import Mailchimp CSV
          </DialogTitle>
          <DialogDescription className="text-white/50 text-xs font-sans">
            Upload a Subscribed, Unsubscribed, or Cleaned export. We will auto-detect
            the file type from the headers. Nothing is written to the contact DB until
            you click <b>Commit import</b>.
          </DialogDescription>
        </DialogHeader>

        {!preview && (
          <div className="py-4">
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              onChange={handleFile}
              className="block w-full text-sm text-white/70 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:uppercase file:tracking-widest file:bg-[#D4AF37] file:text-black hover:file:bg-[#F3C853]"
              data-testid="import-file-input"
            />
            {file && (
              <p className="mt-3 text-xs text-white/50 font-sans">
                Selected: <b>{file.name}</b> · {(file.size / 1024).toFixed(1)} KB
              </p>
            )}
          </div>
        )}

        {preview && (
          <div className="py-2 max-h-[55vh] overflow-y-auto space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div className="rounded-md border border-white/10 p-3">
                <div className="text-[9px] uppercase tracking-widest text-white/40">Detected</div>
                <div className="text-white font-serif text-lg mt-1 capitalize">{preview.detected_status}</div>
              </div>
              <div className="rounded-md border border-white/10 p-3">
                <div className="text-[9px] uppercase tracking-widest text-white/40">Valid</div>
                <div className="text-emerald-300 font-serif text-lg mt-1">{preview.valid_rows}</div>
              </div>
              <div className="rounded-md border border-white/10 p-3">
                <div className="text-[9px] uppercase tracking-widest text-white/40">Invalid</div>
                <div className="text-rose-300 font-serif text-lg mt-1">{preview.invalid_rows}</div>
              </div>
              <div className="rounded-md border border-white/10 p-3">
                <div className="text-[9px] uppercase tracking-widest text-white/40">Already in DB</div>
                <div className="text-amber-300 font-serif text-lg mt-1">{preview.duplicates_in_db}</div>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2 font-sans">
                Header mapping
              </div>
              <div className="rounded-md border border-white/10 bg-black/40 p-3 text-xs grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-4 font-mono">
                {Object.entries(preview.header_mapping || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span className="text-white/60">{k || "(unnamed)"}</span>
                    <span className={v === "(ignored)" ? "text-white/30" : "text-[#D4AF37]"}>→ {v}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2 font-sans">
                Sample rows (first 10)
              </div>
              <div className="rounded-md border border-white/10 bg-black/40 p-3 overflow-x-auto">
                <table className="text-xs text-white/80 font-sans min-w-full">
                  <thead>
                    <tr className="text-[9px] uppercase tracking-widest text-white/40">
                      <th className="text-left px-2 py-1">Email</th>
                      <th className="text-left px-2 py-1">Name</th>
                      <th className="text-left px-2 py-1">Tags</th>
                      <th className="text-left px-2 py-1">Status</th>
                      <th className="text-left px-2 py-1">Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.sample_rows || []).map((r, i) => (
                      <tr key={i} className="border-t border-white/5">
                        <td className="px-2 py-1">{r.email || <span className="text-rose-300">—</span>}</td>
                        <td className="px-2 py-1">{[r.first_name, r.last_name].filter(Boolean).join(" ")}</td>
                        <td className="px-2 py-1">{(r.tags || []).join(", ")}</td>
                        <td className="px-2 py-1 capitalize">{r.subscription_status}</td>
                        <td className="px-2 py-1 text-rose-300">{(r._errors || []).join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {preview.existing_in_db?.length > 0 && (
              <p className="text-xs text-amber-300/80 font-sans">
                <Warning size={12} weight="duotone" className="inline mr-1" />
                {preview.existing_in_db.length} email{preview.existing_in_db.length === 1 ? "" : "s"} already
                exist in the DB — committing will update them without creating duplicates. Unsubscribed and
                cleaned statuses are preserved; the import will <b>never</b> auto-resubscribe them.
              </p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {!preview && (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="border-white/15 bg-transparent text-white/70 hover:bg-white/5"
                data-testid="import-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={upload}
                disabled={!file || uploading}
                className="bg-[#D4AF37] hover:bg-[#F3C853] text-black font-sans text-xs uppercase tracking-widest"
                data-testid="import-preview-btn"
              >
                {uploading ? "Analyzing…" : (<><FileArrowUp size={14} className="mr-2" /> Preview</>)}
              </Button>
            </>
          )}
          {preview && (
            <>
              <Button
                variant="outline"
                onClick={reset}
                className="border-white/15 bg-transparent text-white/70 hover:bg-white/5"
                data-testid="import-choose-another-btn"
              >
                Choose another file
              </Button>
              <Button
                onClick={commit}
                disabled={committing || preview.valid_rows === 0}
                className="bg-[#D4AF37] hover:bg-[#F3C853] text-black font-sans text-xs uppercase tracking-widest"
                data-testid="import-commit-btn"
              >
                {committing ? "Committing…" : (<><CheckCircle size={14} className="mr-2" /> Commit import</>)}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Contact add/edit dialog
// ---------------------------------------------------------------------------
function ContactDialog({ open, onOpenChange, contact, onSaved }) {
  const isEdit = !!contact?.id;
  const [form, setForm] = useState({
    email: "", first_name: "", last_name: "", phone: "",
    client_type: "", tags: "", travel_interests: "", notes: "",
    subscription_status: "subscribed",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (contact) {
      setForm({
        email: contact.email || "",
        first_name: contact.first_name || "",
        last_name: contact.last_name || "",
        phone: contact.phone || "",
        client_type: contact.client_type || "",
        tags: (contact.tags || []).join(", "),
        travel_interests: (contact.travel_interests || []).join(", "),
        notes: contact.notes || "",
        subscription_status: contact.subscription_status || "subscribed",
      });
    } else if (open) {
      setForm({
        email: "", first_name: "", last_name: "", phone: "",
        client_type: "", tags: "", travel_interests: "", notes: "",
        subscription_status: "subscribed",
      });
    }
  }, [contact, open]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        travel_interests: form.travel_interests.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (isEdit) {
        delete payload.email;
        await api.patch(`/admin/email-marketing/contacts/${contact.id}`, payload);
      } else {
        delete payload.subscription_status;
        await api.post("/admin/email-marketing/contacts", payload);
      }
      toast.success(isEdit ? "Contact updated" : "Contact added");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-black border border-white/10" data-testid="contact-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-serif text-2xl">
            {isEdit ? "Edit contact" : "Add contact"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div>
            <label className="text-[9px] uppercase tracking-widest text-white/40">Email</label>
            <Input
              type="email"
              value={form.email}
              disabled={isEdit}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={inputCls}
              data-testid="contact-email-input"
            />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-white/40">Phone</label>
            <Input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className={inputCls}
              data-testid="contact-phone-input"
            />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-white/40">First name</label>
            <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={inputCls} data-testid="contact-first-input" />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-white/40">Last name</label>
            <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={inputCls} data-testid="contact-last-input" />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-white/40">Client type</label>
            <Input value={form.client_type} onChange={(e) => setForm({ ...form, client_type: e.target.value })} className={inputCls} data-testid="contact-type-input" placeholder="VIP, Honeymoon, Family…" />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-white/40">Status</label>
            <Select value={form.subscription_status} onValueChange={(v) => setForm({ ...form, subscription_status: v })}>
              <SelectTrigger className={inputCls} data-testid="contact-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="subscribed">Subscribed</SelectItem>
                <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                <SelectItem value="cleaned">Cleaned</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-[9px] uppercase tracking-widest text-white/40">Tags (comma separated)</label>
            <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className={inputCls} data-testid="contact-tags-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[9px] uppercase tracking-widest text-white/40">Travel interests</label>
            <Input value={form.travel_interests} onChange={(e) => setForm({ ...form, travel_interests: e.target.value })} className={inputCls} data-testid="contact-interests-input" placeholder="Beach, Cruises, Luxury…" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[9px] uppercase tracking-widest text-white/40">Notes</label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} min-h-[80px]`} data-testid="contact-notes-input" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/15 bg-transparent text-white/70 hover:bg-white/5" data-testid="contact-cancel-btn">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || !form.email} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black font-sans text-xs uppercase tracking-widest" data-testid="contact-save-btn">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function AdminEmailMarketing() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [importOpen, setImportOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [activeContact, setActiveContact] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/admin/email-marketing/summary"),
        api.get("/admin/email-marketing/contacts", {
          params: {
            q, status: status || undefined, source: source || undefined,
            page, page_size: pageSize,
          },
        }),
      ]);
      setSummary(s.data);
      setRows(c.data.rows || []);
      setTotal(c.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load contacts");
    } finally {
      setLoading(false);
    }
  }, [q, status, source, page]);

  useEffect(() => { reload(); }, [reload]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const removeContact = async (contact) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete ${contact.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/email-marketing/contacts/${contact.id}`);
      toast.success("Contact deleted");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <AgentLayout>
      <div className="mb-6 animate-fade-up" data-testid="admin-email-marketing-page">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.25em] text-white/40 hover:text-[#D4AF37] transition-colors font-sans mb-4"
          data-testid="back-to-admin-link"
        >
          <CaretLeft size={12} weight="bold" /> Back to dashboard
        </Link>
        <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-3 font-sans flex items-center gap-2">
          <EnvelopeSimple size={12} weight="duotone" /> Email Marketing
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-white mb-3">
          Subscriber <span className="italic text-[#D4AF37]">database</span>
        </h1>
        <p className="text-white/60 text-sm max-w-2xl font-sans">
          The centralized contact database that replaces Mailchimp. Import your
          existing audience, manage subscriber status, and send campaigns from
          inside Best Time Travel &amp; Tours.
        </p>
        {/* iter42 — Phase 2 sub-nav between Contacts (current) / Campaigns / Templates */}
        <div className="mt-5 inline-flex items-center gap-1 rounded-lg border border-white/10 bg-black/40 p-1" data-testid="email-sub-nav">
          <span className="px-3 py-1.5 rounded-md bg-[#D4AF37] text-black text-[10px] uppercase tracking-widest font-sans" data-testid="sub-nav-contacts">
            Contacts
          </span>
          <Link to="/admin/email-marketing/campaigns" data-testid="sub-nav-campaigns" className="px-3 py-1.5 rounded-md text-white/60 hover:text-[#D4AF37] text-[10px] uppercase tracking-widest font-sans transition-colors">
            Campaigns
          </Link>
          <Link to="/admin/email-marketing/templates" data-testid="sub-nav-templates" className="px-3 py-1.5 rounded-md text-white/60 hover:text-[#D4AF37] text-[10px] uppercase tracking-widest font-sans transition-colors">
            Templates
          </Link>
        </div>
      </div>

      {/* KPI grid */}
      {summary && (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-8"
          data-testid="kpi-grid"
        >
          <Kpi label="Total contacts" value={summary.total_contacts} icon={Users} tone="gold" testId="kpi-total" />
          <Kpi label="Subscribed" value={summary.subscribed} icon={CheckCircle} tone="emerald" testId="kpi-subscribed" />
          <Kpi label="Unsubscribed" value={summary.unsubscribed} icon={Prohibit} tone="rose" testId="kpi-unsubscribed" />
          <Kpi label="Cleaned" value={summary.cleaned} icon={Broom} tone="amber" testId="kpi-cleaned" />
          <Kpi label="Advisor clients" value={summary.advisor_clients} icon={UserPlus} testId="kpi-advisor-clients" />
          <Kpi label="Campaigns sent" value={summary.campaigns_sent} icon={EnvelopeSimple} testId="kpi-campaigns" />
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search email, name, phone…"
            className={`${inputCls} pl-9`}
            data-testid="contact-search-input"
          />
        </div>
        <Select value={status || "__all__"} onValueChange={(v) => { setStatus(v === "__all__" ? "" : v); setPage(1); }}>
          <SelectTrigger className={`${inputCls} w-[160px]`} data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All statuses</SelectItem>
            <SelectItem value="subscribed">Subscribed</SelectItem>
            <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
            <SelectItem value="cleaned">Cleaned</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        <Select value={source || "__all__"} onValueChange={(v) => { setSource(v === "__all__" ? "" : v); setPage(1); }}>
          <SelectTrigger className={`${inputCls} w-[180px]`} data-testid="source-filter">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All sources</SelectItem>
            {Object.entries(SOURCE_LABEL).map(([v, lbl]) => <SelectItem key={v} value={v}>{lbl}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button
          onClick={() => { setActiveContact(null); setContactDialogOpen(true); }}
          variant="outline"
          className="border-white/15 bg-transparent text-white/80 hover:bg-white/5 text-xs uppercase tracking-widest"
          data-testid="add-contact-btn"
        >
          <UserPlus size={14} className="mr-2" /> Add contact
        </Button>
        <Button
          onClick={() => setImportOpen(true)}
          className="bg-[#D4AF37] hover:bg-[#F3C853] text-black font-sans text-xs uppercase tracking-widest"
          data-testid="import-csv-btn"
        >
          <Upload size={14} className="mr-2" /> Import CSV
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-white/10 bg-black/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-sans" data-testid="contact-table">
            <thead>
              <tr className="text-[9px] uppercase tracking-widest text-white/40 border-b border-white/10">
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-left px-4 py-3">Tags</th>
                <th className="text-left px-4 py-3">Signup</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-white/40 text-xs">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-white/40 text-xs">
                  No contacts yet. Import your Mailchimp CSVs or add a contact manually.
                </td></tr>
              )}
              {!loading && rows.map((r, i) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`contact-row-${i}`}>
                  <td className="px-4 py-3 text-white/90">{r.email}</td>
                  <td className="px-4 py-3 text-white/80">{[r.first_name, r.last_name].filter(Boolean).join(" ") || <span className="text-white/30">—</span>}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[9px] uppercase tracking-widest ${STATUS_CHIP[r.subscription_status] || ""}`}>
                      {r.subscription_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-white/60 text-xs">{SOURCE_LABEL[r.source] || r.source}</td>
                  <td className="px-4 py-3 text-white/70 text-xs">
                    {(r.tags || []).slice(0, 3).map((t) => (
                      <span key={t} className="inline-flex items-center gap-0.5 mr-1 mb-1 px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[10px]">
                        <Tag size={9} weight="duotone" />{t}
                      </span>
                    ))}
                    {(r.tags || []).length > 3 && (
                      <span className="text-white/40 text-[10px]">+{r.tags.length - 3}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-white/50 text-xs">
                    {r.signup_date ? new Date(r.signup_date).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => { setActiveContact(r); setContactDialogOpen(true); }} className="h-8 w-8 p-0 text-white/50 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10" title="Edit" data-testid={`contact-edit-${i}`}>
                      <PencilSimple size={14} weight="duotone" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => removeContact(r)} className="h-8 w-8 p-0 text-white/50 hover:text-rose-400 hover:bg-rose-500/10" title="Delete" data-testid={`contact-delete-${i}`}>
                      <Trash size={14} weight="duotone" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/10 text-xs text-white/50 font-sans">
          <span>
            Showing {rows.length === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="h-7 border-white/15 bg-transparent text-white/70 hover:bg-white/5 text-xs" data-testid="page-prev">
              Prev
            </Button>
            <span className="px-2 py-1 text-white/60">Page {page} / {totalPages}</span>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="h-7 border-white/15 bg-transparent text-white/70 hover:bg-white/5 text-xs" data-testid="page-next">
              Next
            </Button>
          </div>
        </div>
      </div>

      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onCommitted={reload} />
      <ContactDialog
        open={contactDialogOpen}
        onOpenChange={(v) => { setContactDialogOpen(v); if (!v) setActiveContact(null); }}
        contact={activeContact}
        onSaved={reload}
      />
    </AgentLayout>
  );
}
